import { useEffect, useRef, type ReactNode } from 'react'

import { Icon } from './Icon'
import styles from './Modal.module.css'
import { useScrollLock } from './useScrollLock'

export interface ModalProps {
  open: boolean
  title: ReactNode
  hint?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  onClose: () => void
  inline?: boolean
}

export function Modal({ open, title, hint, children, footer, onClose, inline = false }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  useScrollLock(open && !inline)

  useEffect(() => {
    if (!open) return
    dialogRef.current?.focus()
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose, open])

  if (!open) return null
  return (
    <div
      className={`${styles.scrim} ${inline ? styles.inline : ''}`}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div ref={dialogRef} className={styles.dialog} role="dialog" aria-modal={!inline} tabIndex={-1}>
        {inline ? null : (
          <button type="button" className={styles.close} aria-label="关闭" onClick={onClose}>
            <Icon name="x" size={18} strokeWidth={1.9} />
          </button>
        )}
        <div className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
          {hint ? <p className={styles.hint}>{hint}</p> : null}
        </div>
        {children ? <div className={styles.body}>{children}</div> : null}
        {footer ? <div className={styles.foot}>{footer}</div> : null}
      </div>
    </div>
  )
}
